/**
 * <site-header> custom element.
 *
 * Behavior:
 *  - Opens/closes mobile menu drawer via <dialog>
 *  - Toggles a data-scrolled attribute when the page scrolls past a threshold
 *    so the sticky header can grow a shadow
 *  - Listens for the global "cart:updated" event to refresh the cart count badge
 */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-drawer]');
    this.menuToggle = this.querySelector('[data-action="menu-toggle"]');
    this.menuClose = this.querySelector('[data-action="menu-close"]');
    this.cartButton = this.querySelector('[data-action="cart-open"]');
    this.cartCount = this.querySelector('[data-cart-count]');

    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => this.openMenu());
    }
    if (this.menuClose) {
      this.menuClose.addEventListener('click', () => this.closeMenu());
    }
    if (this.drawer) {
      this.drawer.addEventListener('close', () => {
        if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', 'false');
      });
    }
    // Cart opener is handled by <cart-drawer> via document-level [data-action="cart-open"] delegation.

    this._onScroll = this._onScroll.bind(this);
    if (this.classList.contains('site-header--sticky')) {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
    }

    document.addEventListener('cart:updated', (e) => this.updateCount(e.detail?.item_count));
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
  }

  openMenu() {
    if (!this.drawer) return;
    this.drawer.showModal();
    if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', 'true');
  }

  closeMenu() {
    if (!this.drawer) return;
    this.drawer.close();
    if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', 'false');
  }

  _onScroll() {
    const scrolled = window.scrollY > 4;
    this.setAttribute('data-scrolled', scrolled ? 'true' : 'false');
  }

  updateCount(count) {
    if (!this.cartCount || typeof count !== 'number') return;
    this.cartCount.textContent = count;
    if (count > 0) this.cartCount.removeAttribute('hidden');
    else this.cartCount.setAttribute('hidden', '');
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}

class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-mega-trigger]')];
    this.openTimer = null;
    this.closeTimer = null;
    this.activeTrigger = null;

    this.items.forEach((trigger) => {
      const item = trigger.closest('.site-header__nav-item');
      const panel = item?.querySelector('[data-mega-panel]');
      if (!panel) return;

      trigger.addEventListener('click', (e) => {
        // Parent links navigate by default. Only intercept on the chevron
        // (or when the trigger has no real href) to toggle the dropdown.
        const href = trigger.getAttribute('href');
        const isChevron = e.target.closest('.site-header__nav-chev');
        const noNav = !href || href === '#' || href === '';
        if (isChevron || noNav) {
          e.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          this.closeAll();
          if (!isOpen) this.open(trigger, panel);
        }
      });
      trigger.addEventListener('mouseenter', () => this.scheduleOpen(trigger, panel));
      trigger.addEventListener('focus', () => this.open(trigger, panel));
      item.addEventListener('mouseleave', () => this.scheduleClose());
      panel.addEventListener('mouseenter', () => this.cancelClose());
      panel.addEventListener('mouseleave', () => this.scheduleClose());
    });

    this._onDocClick = (e) => {
      if (!this.contains(e.target)) this.closeAll();
    };
    this._onKey = (e) => {
      if (e.key === 'Escape' && this.activeTrigger) {
        this.activeTrigger.focus();
        this.closeAll();
      }
    };
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  scheduleOpen(trigger, panel) {
    this.cancelClose();
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => this.open(trigger, panel), 120);
  }

  scheduleClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.closeAll(), 180);
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
  }

  open(trigger, panel) {
    if (this.activeTrigger && this.activeTrigger !== trigger) this.closeAll();
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.activeTrigger = trigger;
  }

  closeAll() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.items.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const panel = trigger.closest('.site-header__nav-item')?.querySelector('[data-mega-panel]');
      if (panel) panel.hidden = true;
    });
    this.activeTrigger = null;
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}
